import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { Select } from '@/components/ui/Select';
import { TextField } from '@/components/ui/TextField';
import type { CatalogItem } from '@/lib/quality-catalogs';

import type { DocumentOrigin } from '../types';
import type { DocumentFormState } from '../use-document-form';
import { CatalogField } from './CatalogField';
import { CodeField } from './CodeField';
import { OwnerField } from './OwnerField';

// Campos del alta (spec G3 D11), en el orden acordado: tipo, proceso, código propuesto
// (y módulo SAP si aplica), nombre, origen y dueño de proceso. Los usan "Nuevo documento"
// y la aprobación de una solicitud.

type Catalog = Pick<UseQueryResult<CatalogItem[]>, 'data' | 'isError' | 'refetch'>;

export function DocumentFields({
  state,
  types,
  areas,
  showOrigin = true,
}: {
  state: DocumentFormState;
  types: Catalog;
  areas: Catalog;
  showOrigin?: boolean;
}): ReactElement {
  const { form } = state;
  return (
    <>
      <CatalogField
        label="Tipo de documento"
        placeholder="Selecciona un tipo…"
        query={types}
        value={form.documentTypeId}
        onChange={state.setType}
      />
      <CatalogField
        label="Proceso"
        placeholder="Selecciona un proceso…"
        query={areas}
        value={form.processAreaId}
        onChange={state.setArea}
      />
      <CodeField
        view={state.view}
        editing={form.editing}
        code={form.code}
        error={state.codeMsg}
        showSap={state.isSap || form.sapModule !== ''}
        sapModule={form.sapModule}
        onSapModule={state.setSapModule}
        onCode={state.setCode}
        onEdit={state.editCode}
        onUseProposed={state.useProposed}
        onRetry={state.retryProposal}
      />
      <TextField
        label="Nombre"
        value={form.title}
        maxLength={200}
        placeholder="Liberación de producto terminado"
        error={state.titleMsg}
        onChange={state.setTitle}
      />
      {showOrigin && (
        <label className="fld">
          <span className="fld-lab">Origen</span>
          <Select
            value={form.origin}
            options={[
              { value: 'interno', label: 'Interno (lo elabora la organización)' },
              { value: 'externo', label: 'Externo (norma, proveedor o cliente)' },
            ]}
            onChange={(v) => state.setOrigin(v as DocumentOrigin)}
          />
        </label>
      )}
      <OwnerField value={form.owner} onChange={state.setOwner} />
    </>
  );
}
